export type StepFunctionResourceInventory = {
  lambdaArns: string[];
  stateMachineArns: string[];
  dynamoTableNames: string[];
  s3Buckets: string[];
  serviceIntegrations: string[];
  otherArns: string[];
  parseError?: string;
};

function safeString(v: unknown): string | null {
  if (typeof v === 'string') return v;
  return null;
}

function collectStringsDeep(input: unknown, out: string[]) {
  if (input === null || input === undefined) return;
  if (typeof input === 'string') {
    out.push(input);
    return;
  }
  if (Array.isArray(input)) {
    for (const v of input) collectStringsDeep(v, out);
    return;
  }
  if (typeof input === 'object') {
    for (const v of Object.values(input as Record<string, unknown>)) {
      collectStringsDeep(v, out);
    }
  }
}

function findAllMatches(text: string, re: RegExp) {
  return Array.from(text.matchAll(re))
    .map(m => m[0])
    .filter(Boolean);
}

function extractFromText(definitionText: string): StepFunctionResourceInventory {
  const text = definitionText ?? '';

  const lambdaArns = [
    ...new Set(
      findAllMatches(text, /arn:aws:lambda:[^"'\s]+:function:[A-Za-z0-9-_]+(?::[^"'\s]+)?/g).map(
        m => m.replace(/(arn:aws:lambda:[^"'\s]+:function:[A-Za-z0-9-_]+)(?::[^"'\s]+)?$/, '$1'),
      ),
    ),
  ];

  const stateMachineArns = [
    ...new Set(findAllMatches(text, /arn:aws:states:[^"'\s]+:stateMachine:[A-Za-z0-9-_]+/g)),
  ];

  const serviceIntegrations = [
    ...new Set(findAllMatches(text, /arn:aws:states:::[A-Za-z0-9-_.:]+/g)),
  ];

  // S3 bucket ARNs or bucket-like tokens
  const s3BucketsFromArn = [
    ...new Set(
      findAllMatches(text, /arn:aws:s3:::[A-Za-z0-9.\-_]+/g).map(v =>
        v.replace(/^arn:aws:s3:::/, ''),
      ),
    ),
  ];
  const bucketTokens = [
    ...new Set(
      findAllMatches(
        text,
        /\b(?:kms-(?:customer-data|lexipol-data|manual)-migration-[a-z]+|phoenix-media-[a-z]+|document-management-service-[a-z0-9-]+|(?:archived|staged|issued)-manuals-[a-z]+|legacy-archived-manuals-[a-z]+|archive-manual-migration-[a-z]+)\b/g,
      ),
    ),
  ];

  const otherArns = [...new Set(findAllMatches(text, /arn:aws:[A-Za-z0-9-]+:[^"'\s]+/g))];

  // Remove anything we already categorized so `otherArns` stays truly "other".
  // Also exclude Lambda ARNs entirely from `otherArns` (they have their own UI section).
  const categorized = new Set<string>([...lambdaArns, ...stateMachineArns, ...serviceIntegrations]);
  const filteredOtherArns = otherArns.filter(
    a => !categorized.has(a) && !a.startsWith('arn:aws:lambda:'),
  );

  return {
    lambdaArns,
    stateMachineArns,
    dynamoTableNames: [],
    s3Buckets: [...new Set([...s3BucketsFromArn, ...bucketTokens])],
    serviceIntegrations,
    otherArns: filteredOtherArns,
  };
}

/**
 * Best-effort inventory of resources referenced by a Step Function definition.
 *
 * - If JSON parses: traverse object tree to find key fields (TableName, Bucket) and any embedded strings.
 * - If JSON doesn't parse: fall back to regex scanning of raw text.
 */
export function scanStepFunctionDefinitionResources(
  definitionText: string,
): StepFunctionResourceInventory {
  const raw = definitionText ?? '';
  if (!raw.trim()) {
    return {
      lambdaArns: [],
      stateMachineArns: [],
      dynamoTableNames: [],
      s3Buckets: [],
      serviceIntegrations: [],
      otherArns: [],
    };
  }

  try {
    const parsed = JSON.parse(raw);

    const strings: string[] = [];
    collectStringsDeep(parsed, strings);

    const dynamoTableNames: string[] = [];
    const s3Buckets: string[] = [];
    const serviceIntegrations: string[] = [];

    const visit = (node: unknown) => {
      if (node === null || node === undefined) return;
      if (Array.isArray(node)) {
        for (const v of node) visit(v);
        return;
      }
      if (typeof node !== 'object') return;

      for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
        if (k === 'TableName') {
          const s = safeString(v);
          if (s) dynamoTableNames.push(s);
        }
        if (k === 'Bucket') {
          const s = safeString(v);
          if (s) s3Buckets.push(s);
        }
        if (k === 'Resource') {
          const s = safeString(v);
          if (s && s.startsWith('arn:aws:states:::')) serviceIntegrations.push(s);
        }
        visit(v);
      }
    };
    visit(parsed);

    const fromText = extractFromText(strings.join('\n'));

    const mergedDynamo = [...new Set([...dynamoTableNames, ...fromText.dynamoTableNames])];
    const mergedBuckets = [...new Set([...s3Buckets, ...fromText.s3Buckets])];
    const mergedIntegrations = [
      ...new Set([...serviceIntegrations, ...fromText.serviceIntegrations]),
    ];

    // Re-filter other ARNs after we merge categories (to avoid duplicates/out-of-category entries).
    const categorized = new Set<string>([
      ...fromText.lambdaArns,
      ...fromText.stateMachineArns,
      ...mergedIntegrations,
    ]);
    const mergedOtherArns = [
      ...new Set([...fromText.otherArns, ...extractFromText(raw).otherArns]),
    ].filter(a => !categorized.has(a) && !a.startsWith('arn:aws:lambda:'));

    return {
      ...fromText,
      dynamoTableNames: mergedDynamo,
      s3Buckets: mergedBuckets,
      serviceIntegrations: mergedIntegrations,
      otherArns: mergedOtherArns,
    };
  } catch (e) {
    return {
      ...extractFromText(raw),
      parseError: e instanceof Error ? e.message : String(e),
    };
  }
}
